import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout from '../components/layout/AuthLayout';
import { BRAND, fieldSx, primaryButtonSx } from '../styles/ui';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

const FirstTimeLogin: React.FC = () => {
  const { login, error, isLoading, isAuthenticated, changePassword, respondToAuthChallenge } = useAuth();
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const username = searchParams.get('username') || '';
  // Password requirements check
  const validatePassword = (password: string) => {
    const requirements = {
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
    return {
      ...requirements,
      isValid: Object.values(requirements).every(Boolean)
    };
  };

  // Form validation schema
  const validationSchema = Yup.object({
    username: Yup.string()
      .email('Enter a valid email')
      .required('Email is required'),
    fullName: Yup.string()
      .required('Full name is required')
      .min(2, 'Name must be at least 2 characters'),
    newPassword: Yup.string()
      .required('New password is required')
      .test('password-requirements', 'Password does not meet requirements', 
        (value) => validatePassword(value || '').isValid
      ),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], 'Passwords must match')
      .required('Please confirm your new password')
  });

  // Formik setup
  const formik = useFormik({
    initialValues: {
      username: username,
      fullName: '',
      newPassword: '',
      confirmPassword: ''
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setLoginError(null);
      
      try {
        // Get the session from URL params or location state
        const session = searchParams.get('session') || (location.state as any)?.session;
        
        if (!session) {
          throw new Error('No session found. Please request a new password reset.');
        }
        
        // Complete the new password challenge with user attributes
        await respondToAuthChallenge(
          values.username,
          values.newPassword,
          session,
          {
            name: values.fullName
          }
        );
        
        // After successful password change, log in with the new password
        await login(values.username, values.newPassword);
        navigate('/profile');
      } catch (error: any) {
        console.error('Password change error:', error);
        setLoginError(error.message || 'Failed to update password. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  });

  const passwordRequirements = validatePassword(formik.values.newPassword);

  const requirementItems = [
    { met: passwordRequirements.minLength, label: 'At least 8 characters' },
    { met: passwordRequirements.hasUpperCase, label: 'At least one uppercase letter' },
    { met: passwordRequirements.hasLowerCase, label: 'At least one lowercase letter' },
    { met: passwordRequirements.hasNumber, label: 'At least one number' },
    { met: passwordRequirements.hasSpecialChar, label: 'At least one special character' }
  ];

  return (
    <AuthLayout
      title="Set Up Your Account"
      subtitle="Please set a new password for your account"
      footerActions={
        <Button
          type="submit"
          form="first-login-form"
          variant="contained"
          disabled={isSubmitting || !formik.isValid}
          sx={primaryButtonSx}
        >
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Set New Password'}
        </Button>
      }
    >
      {loginError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {loginError}
        </Alert>
      )}

      <form id="first-login-form" onSubmit={formik.handleSubmit} noValidate>
        <TextField
          margin="normal"
          fullWidth
          id="username"
          label="Email"
          name="username"
          autoComplete="email"
          value={formik.values.username}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.username && Boolean(formik.errors.username)}
          helperText={formik.touched.username && formik.errors.username ? String(formik.errors.username) : ''}
          disabled={!!username}
          sx={{
            ...fieldSx,
            '& .MuiOutlinedInput-root.Mui-disabled': {
              '& fieldset': { borderColor: BRAND.lineStrong },
            },
            '& .MuiInputLabel-root.Mui-disabled': { color: BRAND.muted },
            '& .MuiOutlinedInput-input.Mui-disabled': {
              color: BRAND.ink,
              WebkitTextFillColor: BRAND.ink
            },
          }}
        />

        <TextField
          margin="normal"
          fullWidth
          id="fullName"
          name="fullName"
          label="Full Name"
          autoComplete="name"
          value={formik.values.fullName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.fullName && Boolean(formik.errors.fullName)}
          helperText={formik.touched.fullName && formik.errors.fullName ? String(formik.errors.fullName) : ''}
          sx={fieldSx}
        />

        <TextField
          margin="normal"
          fullWidth
          id="newPassword"
          name="newPassword"
          label="New Password"
          type="password"
          autoComplete="new-password"
          value={formik.values.newPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
          helperText={formik.touched.newPassword && formik.errors.newPassword ? String(formik.errors.newPassword) : ''}
          sx={fieldSx}
        />

        <TextField
          margin="normal"
          fullWidth
          id="confirmPassword"
          name="confirmPassword"
          label="Confirm New Password"
          type="password"
          autoComplete="new-password"
          value={formik.values.confirmPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
          helperText={formik.touched.confirmPassword && formik.errors.confirmPassword ? String(formik.errors.confirmPassword) : ''}
          sx={fieldSx}
        />

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ color: BRAND.ink, mb: 1 }}>
            Password Requirements:
          </Typography>
          <List dense sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3 }}>
            {requirementItems.map(({ met, label }) => (
              <ListItem key={label} disableGutters>
                <ListItemIcon sx={{ minWidth: 30 }}>
                  {met ?
                    <CheckCircleIcon sx={{ color: BRAND.accent }} fontSize="small" /> :
                    <ErrorIcon sx={{ color: BRAND.danger }} fontSize="small" />}
                </ListItemIcon>
                <ListItemText primary={label} sx={{ '& .MuiListItemText-primary': { color: BRAND.ink } }} />
              </ListItem>
            ))}
          </List>
        </Box>
      </form>
    </AuthLayout>
  );
};

export default FirstTimeLogin;
